import React from 'react';
import { Box, Typography, Button, IconButton, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';

export interface PaymentSplit {
  id: string;
  mode: string;
  amount: number;
  reference?: string;
  date?: string;
}

export interface BillingDialogPaymentSectionProps {
  paymentSplits: PaymentSplit[];
  setPaymentSplits: (splits: PaymentSplit[]) => void;
  paymentModes: { value: string; label: string }[];
  orderItems: any[];
  formatCurrency: (value: number) => string;
  calculateTotalDiscount: () => number;
  calculateTotalDue: () => number;
  calculateAmountPaid: () => number;
  calculateRemainingDue: () => number;
  comment: string;
  setComment: (comment: string) => void;
  totalPreviouslyPaid: number;
  distributePaidAmount: (totalPaid: number) => void;
}

const BillingDialogPaymentSection: React.FC<BillingDialogPaymentSectionProps> = ({
  paymentSplits,
  setPaymentSplits,
  paymentModes,
  orderItems,
  formatCurrency,
  calculateTotalDiscount,
  calculateTotalDue,
  calculateAmountPaid,
  calculateRemainingDue,
  comment,
  setComment,
  
  totalPreviouslyPaid,
}) => {
  const [editingAmount, setEditingAmount] = React.useState<{ [id: string]: string }>({});

  return (
    <Box mt={3}>
    <Typography variant="h6" gutterBottom>
      Payment Details
    </Typography>
    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4} mt={2}>
      {/* Left Column: Payment Methods */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {paymentSplits.length > 0 ? (
          paymentSplits.map((payment, index) => (
            <Box key={payment.id} display="grid" alignItems="center" gap={2} gridTemplateColumns="150px 150px 180px auto">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={payment.mode}
                  label="Payment Mode"
                  onChange={(e) => {
                    const newPayments = [...paymentSplits];
                    newPayments[index].mode = e.target.value;
                    setPaymentSplits(newPayments);
                  }}
                >
                  {paymentModes.map((mode) => {
                    // Disable if this mode is already used in another split
                    const isUsed = paymentSplits.some((split, i) => split.mode === mode.value && i !== index);
                    return (
                      <MenuItem key={mode.value} value={mode.value} disabled={isUsed}>
                        {mode.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Amount"
                type="number"
                value={editingAmount?.[payment.id] ?? (payment.amount === 0 ? '' : payment.amount)}
                onChange={e => {
                  setEditingAmount(prev => ({ ...prev, [payment.id]: e.target.value }));
                }}
                onBlur={e => {
                  const value = e.target.value;
                  const newAmount = value === '' ? 0 : (parseFloat(value) || 0);
                  const newPayments = [...paymentSplits];
                  newPayments[index].amount = newAmount;
                  setPaymentSplits(newPayments);
                  const totalPaid = newPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
                  // distributePaidAmount removed (totalPaid);
                  setEditingAmount(prev => {
                    const { [payment.id]: _, ...rest } = prev;
                    return rest;
                  });
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={{ width: 150 }}
              />
              {payment.mode !== 'CASH' ? (
                <TextField
                  size="small"
                  label="Reference Number"
                  value={payment.reference || ''}
                  onChange={e => {
                    const newPayments = [...paymentSplits];
                    newPayments[index].reference = e.target.value;
                    setPaymentSplits(newPayments);
                  }}
                  required
                  sx={{ width: 180 }}
                  error={!payment.reference}
                  helperText={payment.reference === '' && payment.mode !== 'CASH' ? "Required" : " "}
                />
              ) : (
                <Box sx={{ width: 180 }} />
              )} 
              {paymentSplits.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const newPayments = paymentSplits.filter((_, i) => i !== index);
                    setPaymentSplits(newPayments);
                  }}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary">
            No payment methods added
          </Typography>
        )}
        <Button 
          variant="outlined" 
          size="small" 
          startIcon={<AddIcon />}
          onClick={() => {
  const hasCash = paymentSplits.some(split => split.mode === 'CASH');
  let nextMode = 'CASH';
  if (hasCash) {
    // Pick first available mode that is not 'CASH'
    const nonCash = paymentModes.find(m => m.value !== 'CASH');
    nextMode = nonCash ? nonCash.value : '';
  }
  if (nextMode) {
    setPaymentSplits([
      ...paymentSplits,
      {
        id: uuidv4(),
        mode: nextMode,
        amount: 0,
        reference: '',
        date: new Date().toISOString()
      }
    ]);
  }
}}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Payment Method
        </Button>
      
        {/* Comment Section */}
        <TextField
          label="Comment (optional)"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          size="small"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{ mt: 2 }}
        />
      </Box>
      {/* Right Column: Order Summary */}
      <Box sx={{ 
        minWidth: 250, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1, 
        p: 2, 
        bgcolor: 'grey.50', 
        borderRadius: 1,
        height: 'fit-content'
      }}>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">Subtotal:</Typography>
          <Typography variant="body2">
            {formatCurrency(orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0))}
          </Typography>
        </Box>
        {orderItems.some(item => item.discountValue > 0) && (
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">Discount:</Typography>
            <Typography variant="body2" color="error">
              -{formatCurrency(orderItems.reduce((sum, item) => sum + (item.discountValue || 0), 0))}
            </Typography>
          </Box>
        )}
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="primary">Previously Paid (static):</Typography>
          <Typography variant="body2" color="primary"><strong>{formatCurrency(totalPreviouslyPaid)}</strong></Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Total:</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {formatCurrency(orderItems.reduce((sum, item) => sum + ((item.price * item.quantity) - (item.discountValue || 0)), 0) - totalPreviouslyPaid)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="body2" color="primary">Paid:</Typography>
          <Typography variant="body2" color="primary">
            <strong>{formatCurrency(paymentSplits.reduce((sum, p) => sum + p.amount, 0))}</strong>
          </Typography>
        </Box>
        {/* Balance/Change Due */}
        <Box display="flex" justifyContent="space-between" mt={1} borderTop={1} borderColor="divider" pt={1}>
          <Typography 
            variant="body2" 
            fontWeight="bold"
            color={calculateRemainingDue() <= 0 ? 'success.main' : 'error.main'}
          >
            {calculateRemainingDue() <= 0 ? 'Change Due:' : 'Balance Due:'}
          </Typography>
          <Typography 
            variant="body2" 
            fontWeight="bold"
            color={calculateRemainingDue() <= 0 ? 'success.main' : 'error.main'}
          >
            {calculateRemainingDue() < 0 ? '+' : calculateRemainingDue() > 0 ? '-' : ''}{formatCurrency(Math.abs(calculateRemainingDue()))}
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
  );
};

export default BillingDialogPaymentSection;
